/**
 * Terms of Service Page
 *
 * Legal terms governing use of ContentSage.
 */

import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import ContentSageLogo from '../components/ContentSageLogo';

const LAST_UPDATED = 'March 2, 2026';
const COMPANY_NAME = 'ContentSage';
const SUPPORT_EMAIL = 'support@contentsage.ai';

export default function TermsPage() {
  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="sticky top-0 bg-white/80 backdrop-blur-xl border-b border-gray-200 z-50">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center">
            <ContentSageLogo size={28} showText={true} />
          </Link>
          <Link
            to="/"
            className="flex items-center space-x-2 text-gray-500 hover:text-gray-900 transition-colors text-sm"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Back</span>
          </Link>
        </div>
      </nav>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-6 py-12">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Terms of Service</h1>
        <p className="text-sm text-gray-500 mb-8">Last updated: {LAST_UPDATED}</p>

        <div className="prose prose-gray max-w-none space-y-6 text-gray-700 text-sm leading-relaxed">
          <section>
            <h2 className="text-lg font-semibold text-gray-900 mt-8 mb-3">1. Acceptance of Terms</h2>
            <p>
              By accessing or using {COMPANY_NAME} ("the Service"), you agree to be bound by these Terms
              of Service. If you do not agree, do not use the Service.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-gray-900 mt-8 mb-3">2. Description of Service</h2>
            <p>
              {COMPANY_NAME} is an AI-powered content generation platform that helps users create blog posts,
              business plans, website copy, and other written content. The Service includes web and mobile
              applications, APIs, and integrations with third-party platforms.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-gray-900 mt-8 mb-3">3. User Accounts</h2>
            <p>
              You must create an account to use the Service. You are responsible for maintaining the
              confidentiality of your account credentials and for all activity under your account. You must
              provide accurate information and keep it up to date.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-gray-900 mt-8 mb-3">4. Credits and Payments</h2>
            <p>
              The Service uses a credit-based system. Credits are consumed when generating content.
              Subscription credits reset each billing period and do not roll over. One-time credit packs
              are valid for 2 years from purchase. All purchases are final and non-refundable except as
              required by law.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-gray-900 mt-8 mb-3">5. Acceptable Use</h2>
            <p>You agree not to:</p>
            <ul className="list-disc pl-5 space-y-1 mt-2">
              <li>Use the Service to generate harmful, illegal, or misleading content</li>
              <li>Attempt to reverse engineer, decompile, or disassemble the Service</li>
              <li>Circumvent credit limits or exploit the Service beyond intended use</li>
              <li>Share your account credentials with unauthorized parties</li>
              <li>Use automated tools to access the Service beyond provided APIs</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-gray-900 mt-8 mb-3">6. Intellectual Property</h2>
            <p>
              Content generated using the Service is owned by you, the user. However, you acknowledge that
              AI-generated content may not be eligible for copyright protection in all jurisdictions. The
              Service itself, including its software, design, and branding, remains the property of {COMPANY_NAME}.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-gray-900 mt-8 mb-3">7. Third-Party Integrations</h2>
            <p>
              The Service integrates with third-party platforms (WordPress, Ghost, Wix, etc.). Your use of
              those platforms is governed by their respective terms. We are not responsible for third-party
              service availability or changes.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-gray-900 mt-8 mb-3">8. Limitation of Liability</h2>
            <p>
              The Service is provided "as is" without warranties of any kind. {COMPANY_NAME} shall not be
              liable for any indirect, incidental, or consequential damages arising from your use of the Service.
              Our total liability shall not exceed the amount you paid in the 12 months preceding the claim.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-gray-900 mt-8 mb-3">9. Termination</h2>
            <p>
              We may suspend or terminate your account if you violate these Terms. You may cancel your
              account at any time. Upon termination, your right to use the Service ceases immediately.
              Unused credits are forfeited upon account cancellation.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-gray-900 mt-8 mb-3">10. Changes to Terms</h2>
            <p>
              We may update these Terms from time to time. Continued use of the Service after changes
              constitutes acceptance of the revised Terms. We will notify users of material changes via
              email or in-app notification.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-gray-900 mt-8 mb-3">11. Contact</h2>
            <p>
              For questions about these Terms, contact us at{' '}
              <a href={`mailto:${SUPPORT_EMAIL}`} className="text-indigo-600 hover:text-indigo-700 underline">
                {SUPPORT_EMAIL}
              </a>.
            </p>
          </section>
        </div>
      </main>
    </div>
  );
}
